/**
 * Transfer editor container
 */
import {
  Component,
  HostListener,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BaseEditorContainerComponent } from '@app/containers/base';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import { Observable, Subscription } from 'rxjs';
import { forEach, get, head, includes, isEmpty, isEqual, merge } from 'lodash';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import {
  IBaseEntityType,
  IFormRules,
  IGridInventoryItemVenueTotalFormData,
  IPageJumpRouteState,
  ITransferFormFieldPath,
} from '@shared/types';
import { UiActions } from '@store/actions';
import { STATE_KEYS, TransferService } from '@shared/services';
import { getFormFieldValue } from '@components/form/services/schema-form/schema-form-functions';
import { TransferDestinationsFormService } from '@shared/services/transfer-destinations/transfer-destinations.form.service';
import { TRANSFER_MODULE } from '@app/routes';
import { select } from '@angular-redux/store';
import { decimalNumber } from '@app/shared/common';
import { AUTO_RESTOCK } from '@app/shared/services/transfer/transfer-constants';
import { Router } from '@angular/router';
import { getObservableValue, judgeScanned } from '@shared/utils';
import { KEY_CODES } from '@config/global-constants';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-transfer-edit-container',
  templateUrl: '../../../base/editor/xux-edit.base.container.html',
  styleUrls: ['../../../base/editor/xux-edit.base.container.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TransferDestinationsContainerComponent
  extends BaseEditorContainerComponent
  implements OnInit
{
  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  @lazyInject(TransferDestinationsFormService)
  public entityFormService: TransferDestinationsFormService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(Router) public router: Router;

  public xuxTheme: boolean = true;
  public formRules: IFormRules;
  public entityIdPath: string = 'transaction_id';
  public title: string = '';
  public transferAddUri: string = `/${TRANSFER_MODULE}/add`;
  public formFieldPath: ITransferFormFieldPath =
    this.entityFormService.formFieldPath;
  public routeState: any;
  public totalCost: number;
  public transferAutoRestockUri: string = `/${TRANSFER_MODULE}/auto-restock`;

  @HostListener('window:keydown', ['$event'])
  public onClick($event: KeyboardEvent): void {
    this.entityFormService.scanningService.keyTimes.push(Date.now());
    if (
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], $event.key) &&
      judgeScanned(this.entityFormService.scanningService.keyTimes)
    ) {
      $event.preventDefault();
    }
  }

  public ngOnInit(): void {
    const routeState = getObservableValue(this.routeState$);
    this.formRules = this.entityFormService.createFormRules(
      this,
      routeState?.origin_from,
    );
    super.ngOnInit();
  }

  public async onFormReady(form: FormGroup): Promise<void> {
    super.onFormReady(form, this.entityIdPath);
    this.subscriptions.push(this.initTopButtonBuildSubscription());

    this.subscriptions.push(
      this.routeState$.subscribe((value: IPageJumpRouteState) => {
        this.routeState = merge(value, {
          isFormDirty: value.isFormDirty || this.form.dirty,
        });
        if (!value?.selectedSite) {
          this.uiActions.openRoute(this.selectOriginFromRoute(value));

          return;
        }

        const { selectedSite, storeForm } = value;

        this.entityFormService.initItemTotalsSubscription(
          (item: IGridInventoryItemVenueTotalFormData) => {
            const pinnedBottomData = get(item, 'pinnedBottomData', []);
            this.totalCost = isEmpty(pinnedBottomData)
              ? decimalNumber(0)
              : head(pinnedBottomData).total_cost;
          },
        );
        this.entityFormService.getInventoryItemVenues(
          [storeForm.site_id, selectedSite.id],
          value.selectedSite.inventory_items,
        );
        this.entityFormService.initDataFromRouteState(
          selectedSite,
          storeForm,
          value?.origin_from,
          value?.create_time,
        );
      }),
    );
  }

  public closeBtnClick(): void {
    this.uiActions.removeNotificationsOnPageLeave();
    this.toolActions.removeAllActionStatuses();
    this.uiActions.historyBack({
      state: this.routeState,
    });
  }

  public cancelBtnClick(): void {
    this.closeBtnClick();
  }

  public async saveButtonClickHandler(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    documentItem: IBaseEntityType = this.document,
  ): Promise<void> {
    if (!this.form?.dirty) return;

    const inventoryItemVenuesValue = getFormFieldValue(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    );

    const transferDestinations =
      this.routeState.storeForm.virtual.transfer_destinations;

    forEach(transferDestinations, (item) => {
      if (this.routeState.selectedSite.id === item.id) {
        item.status = TRANSFER_STATUS_VALUE.New;
        item.inventory_items = isEmpty(this.entityFormService.allInventoryItems)
          ? inventoryItemVenuesValue
          : this.entityFormService.allInventoryItems;
        // must use number here, due to decimal.js issue: https://github.com/MikeMcl/decimal.js/issues/224
        item.totalCost = this.totalCost?.valueOf();
      }
    });

    const state = merge(this.routeState, {
      storeForm: {
        virtual: { transfer_destinations: transferDestinations },
      },
      isFormDirty: this.routeState.isFormDirty || this.form.dirty,
    });

    this.form.markAsPristine();

    this.uiActions.openRoute(this.selectOriginFromRoute(state), {
      state,
    });
  }

  /**
   * build page top buttons with form status
   *
   * @returns form status change subscription
   */
  public initTopButtonBuildSubscription(): Subscription {
    return this.entityFormService.isInventoryItemVenuesGridBusy$.subscribe(
      () => {
        this.pageTopButtons$.next([
          {
            ...this.topSaveButtonDefinition,
            caption: this.I18n.Common.Continue,
          },
        ]);
      },
    );
  }

  public selectOriginFromRoute(data): string {
    return isEqual(get(data, 'origin_from'), AUTO_RESTOCK)
      ? this.transferAutoRestockUri
      : this.transferAddUri;
  }
}
